import { t } from "i18next";
import jsPDF from "jspdf";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useEditDocumentMutation,
  useGetSingleDocumentIdQuery,
} from "../../../../features/api/admin/gestisciDocumentiApi";
import { useServiceDocCompositionEmailMutation } from "../../../../features/api/admin/gestisciPraticheApi";
import { ROLE } from "../../../../utils/constants/profiles";
import { formatTextToHTML } from "../../../../utils/functions/textFormatter";
import CoreButton from "../CoreButton/CoreButton";
import CoreModal from "../CoreModal/CoreModal";
import CoreSpinner from "../CoreSpinner/CoreSpinner";
import DocCompositionSettings from "./DocCompositionSettings";
import ExportButton from "./ExportButton";

function parseFirstLevelOnly(jsonString) {
  try {
    // Rimuovi i backslash che causano problemi
    const cleaned = jsonString.replace(/\\/g, "");

    // Parse del primo livello
    const result = JSON.parse(cleaned);

    return result;
  } catch (error) {
    console.error("Errore nel parsing:", error.message);
    return null;
  }
}

// Alternativa ancora più semplice se vuoi essere più selettivo
function parseFirstLevelSimple(jsonString) {
  // Rimuovi solo i backslash davanti alle virgolette e ai caratteri speciali
  return (
    jsonString
      // Converte \n in veri a capo
      .replace(/\\n/g, "\n")
      // Rimuove caratteri problematici come :-*
      .replace(/:-\*/g, " ")
      // Assicurati che ci siano linee vuote prima dei titoli
      .replace(/\n(#{1,6}\s)/g, "\n\n$1")
      // Assicurati che ci siano linee vuote dopo i separatori
      .replace(/---\n/g, "---\n\n")
  );
}

const DocComposition = ({ document }) => {
  const [extractedDocument, setExtractedDocument] = useState();
  const [inputDocument, setInputDocument] = useState();
  const [outputDocument, setOutputDocument] = useState();
  const [editExtDoc, setEditExtDoc] = useState();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const colorPrimary = useSelector((state) => state.app.primaryColor);
  const appConfig = useSelector((state) => state.app.appConfig);
  const role = useSelector((state) => state.app.role);

  const [serviceDocCompositionEmail] = useServiceDocCompositionEmailMutation();

  const { data: singleDoc, isLoading } = useGetSingleDocumentIdQuery(document, {
    skip: !document,
  });

  const [editDocument, { isLoading: isEditLoading }] =
    useEditDocumentMutation();

  const { Id: PracticeUuid } = useParams();

  const openDocCompositionSettingsModal = () => {
    setIsConfirmModalOpen(true);
  };

  useEffect(() => {
    if (singleDoc && singleDoc?.Data?.extractedDocument) {
      try {
        const extractedDocument = singleDoc?.Data?.extractedDocument
          .replace(/\\r\\n/g, "")
          .replace(/\\(?!\\)/g, "") // Rimuove solo le singole \
          .replace(/\\\\/g, "\\"); // Sostituisce doppie barre \\ con una sola \;
        const parseDoc = JSON.parse(extractedDocument);
        const input = parseDoc.input;
        const output = parseDoc.output;

        setInputDocument(input); // Aggiungi questa riga
        setExtractedDocument(output);
      } catch (error) {
        console.error("Errore:", error);
        return null;
      }
    }
  }, [singleDoc]);

  const handleInputChange = (txt) => {
    setEditExtDoc(txt);
  };

  // Funzione per mostrare l'input area
  const showInputArea = (inputText) => {
    if (!inputText) return null;

    return (
      <div className="mb-6">
        <h3
          style={{ color: colorPrimary }}
          className="text-lg font-semibold mb-3"
        >
          Dati di Input
        </h3>
        <div className="border border-gray-300 rounded-lg bg-gray-50 max-h-64 overflow-y-auto">
          <pre className="p-4 text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
            {inputText}
          </pre>
        </div>
      </div>
    );
  };

  // Funzione per esportare HTML direttamente in PDF
  const exportToPDF = () => {
    try {
      // Ottieni il contenuto da esportare
      let contentToExport = editExtDoc || formatTextToHTML(extractedDocument);

      if (!contentToExport) {
        toast.error(t("NoContentToExport") || "Nessun contenuto da esportare");
        return;
      }

      // Fix specifico per i bullet points - sostituisce solo i caratteri problematici
      // contentToExport = contentToExport.replace(/%æ/g, "•");

      // Crea il PDF
      const pdf = new jsPDF("p", "mm", "a4");

      // Aggiungi header con titolo e data
      const title = singleDoc?.Data?.name || "Documento Generato IA";
      const currentDate = new Date().toLocaleDateString("it-IT");

      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text(title, 20, 20);

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(`Data: ${currentDate}`, 20, 30);

      // Prepara l'HTML con stili inline (invariato)
      const styledHTML = `
      <div style="
        font-family: Arial, sans-serif;
        font-size: 12px;
        line-height: 1.6;
        color: #333;
        max-width: 700px;
        margin: 0;
        padding: 0;
      ">
        <style>
          h1, h2, h3, h4, h5, h6 {
            font-weight: bold;
            margin: 20px 0 10px 0;
            color: #2c3e50;
          }
          h1 { font-size: 20px; }
          h2 { font-size: 18px; }
          h3 { font-size: 16px; }
          h4, h5, h6 { font-size: 14px; }
          p {
            margin: 10px 0;
            text-align: justify;
          }
          ul, ol {
            margin: 10px 0;
            padding-left: 20px;
          }
          /* Fix per i bullet points in PDF */
          ul {
            list-style-type: none;
          }
          ul li:before {
            content: "• ";
            margin-right: 8px;
          }
          strong, b {
            font-weight: bold;
          }
          em, i {
            font-style: italic;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
        </style>
        ${contentToExport}
      </div>
    `;

      // Usa il metodo html di jsPDF (invariato)
      pdf.html(styledHTML, {
        callback: function (pdf) {
          // Salva il PDF
          const filename = `${title.replace(/[^a-zA-Z0-9]/g, "_")}_${
            new Date().toISOString().split("T")[0]
          }.pdf`;
          pdf.save(filename);

          toast.success(
            t("DocumentExported") || "Documento esportato con successo"
          );
        },
        x: 20,
        y: 40,
        width: 170, // Larghezza in mm per il contenuto
        windowWidth: 700, // Larghezza della finestra virtuale per il rendering
        margin: [0, 0, 0, 0], // Margini interni
      });
    } catch (error) {
      console.error("Errore durante l'esportazione PDF:", error);
      toast.error(t("ExportError") || "Errore durante l'esportazione");
    }
  };

  const showHTMLDoc = (text) => {
    const htmlContent = formatTextToHTML(text);
    return (
      <div className="input-area-wrapper relative group">
        <div
          className="p-6 
                   border-2 border-dashed border-gray-200 
                   hover:border-blue-300 hover:bg-blue-50/30
                   focus:border-blue-500 focus:bg-white focus:shadow-md
                   transition-all duration-200 
                   cursor-text
                   rounded-md
                   min-h-[3rem]
                   relative prose prose-sm max-w-none
                   prose-headings:text-gray-900 prose-headings:font-semibold
                   prose-p:text-gray-700 prose-p:leading-relaxed
                   prose-strong:text-gray-900 prose-strong:font-semibold
                   prose-ul:text-gray-700 prose-ol:text-gray-700"
          contentEditable
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          onInput={(e) => handleInputChange(e.currentTarget.innerHTML)}
          style={{
            backgroundColor: text ? "transparent" : "rgba(0,0,0,0.02)",
          }}
        />

        {/* Placeholder che appare solo quando il campo è vuoto */}
        {!text && (
          <div className="absolute top-6 left-6 text-gray-400 italic pointer-events-none">
            Clicca per modificare...
          </div>
        )}

        {/* Tooltip che appare solo in hover */}
        <div
          className="absolute -top-10 left-1/2 transform -translate-x-1/2 
                     bg-black text-white text-sm px-3 py-1 rounded 
                     opacity-0 group-hover:opacity-100 
                     transition-opacity duration-300 
                     pointer-events-none
                     whitespace-nowrap
                     z-50
                     shadow-lg"
        >
          Clicca per modificare
        </div>
      </div>
    );
  };

  const handleEdit = () => {
    const id = toast.loading(t("Saving..."), {
      type: "info",
    });
    const bodyData = {
      id: singleDoc?.Data?.id,
      mainCategoryId: singleDoc?.Data?.mainCategoryId,
      categoryId: singleDoc?.Data?.categoryId,
      name: singleDoc?.Data?.name,
      extractedDocument: editExtDoc,
    };

    editDocument(bodyData)
      .unwrap()
      .then((data) => {
        if (data.Result !== 1)
          return toast.update(id, {
            render: data.Exception || t("DocNotEdited"),
            type: "error",
            autoClose: 1500,
            isLoading: false,
          });
        toast.update(id, {
          render: t("DocEdited"),
          type: "success",
          autoClose: 1500,
          isLoading: false,
        });
      })
      .catch(() => {
        toast.update(id, {
          render: t("DocNotEdited"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      });
  };

  const confirmSendEmail = (fidelityLevel) => {
    const id = toast.loading(t("Sending..."), {
      type: "info",
    });

    serviceDocCompositionEmail({
      idPratica: PracticeUuid,
      level: fidelityLevel,
    }) // o solo PracticeUuid se l'API non accetta il fidelityLevel
      .unwrap()
      .then((data) => {
        if (data.Result !== 1)
          return toast.update(id, {
            render: data.Exception || t("DocNotSent"),
            type: "error",
            autoClose: 1500,
            isLoading: false,
          });
        toast.update(id, {
          render: t("DocSent"),
          type: "success",
          autoClose: 1500,
          isLoading: false,
        });
        setIsConfirmModalOpen(false);
      })
      .catch(() => {
        toast.update(id, {
          render: t("DocNotSent"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      });
  };

  if (isLoading) {
    return <CoreSpinner />;
  }

  return (
    <>
      {document && extractedDocument ? (
        <div className="w-full overflow-auto p-4 mb-4">
          <>
            <h2 style={{ color: colorPrimary }} className="text-center mb-6">
              {t("GeneratedIADocument")}
            </h2>
            {/* Input Area - Aggiunta qui */}
            {role === ROLE.ADMIN && showInputArea(inputDocument)}
            {/* Output Area - Esistente */}
            <div>{showHTMLDoc(extractedDocument)}</div>
            <div className="w-full flex justify-center pt-6 gap-x-2">
              <CoreButton
                className="btn-round"
                disabled={!editExtDoc || isEditLoading}
                testo={t("Save")}
                onClick={handleEdit}
              />

              <ExportButton onExportPDF={exportToPDF} />

              {appConfig?.modules?.DEMO?.DocCompBozza && (
                <div>
                  <CoreButton
                    className="btn-round"
                    testo={t("CreateDraft")}
                    onClick={openDocCompositionSettingsModal}
                  />
                  <CoreModal
                    width={"60%"}
                    height={"85%"}
                    state={isConfirmModalOpen}
                    onClose={() => setIsConfirmModalOpen(false)}
                    component={
                      <DocCompositionSettings
                        onSend={confirmSendEmail}
                        onCancel={() => setIsConfirmModalOpen(false)}
                      />
                    }
                  />
                </div>
              )}
            </div>
          </>
        </div>
      ) : (
        <div className="w-full h-2/3 flex flex-col justify-center text-center">
          <h3 className="text-2xl"> {t("GeneratedIADocument")}</h3>
          <p>{t("ErrorRetrievingCategoriesData")}</p>
        </div>
      )}

      {/* Overlay per chiudere il dropdown quando si clicca fuori */}
      {showExportDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowExportDropdown(false)}
        />
      )}
    </>
  );
};

export default DocComposition;
